import { apolloClient } from '@/vendor/apollo-client.ts'
import { catalogQuery } from '@/data/catalogClient.ts'
import {
  ADD_NOVELTY_TO_CUSTOM_LIST,
  ADD_RELEASE_ENTRY_TO_CUSTOM_LIST,
  GET_NOVELTY_ID_BY_RELATION,
  REMOVE_NOVELTY_FROM_CUSTOM_LIST,
  REMOVE_RELEASE_ENTRY_FROM_CUSTOM_LIST,
} from '@/queries/customLists.ts'
import {
  UserCustomListTargetTypeType,
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
} from '../../../__generated__/graphql.ts'
import { getVolumeNumberFromVersion } from '@/utils'
import type { SyncableAction } from '@/stores/sync/createSyncEngine.ts'

const BOX_RELATION_TYPE = 'collection'

// Novelty membership carries the data needed to decide locally whether the
// tracked thing is now collected/read, so auto-removal needs no server lookup.
// volumes = own-entry volume numbers (volume novelty); relationId = the
// ReleaseItemCollection id used as the miscOwnedMap key (box novelty).
export type NoveltyMeta = {
  entryId: string
  relationType: string
  relationId: string
  volumes: string[]
  collectionKind?: string
}

// entityKey is the thing a list tracks: a ReleaseEntry id for releaseEntry
// lists, a Novelty id for novelty lists. itemId is the join-row id needed to
// delete a membership; it is null for an optimistic add until the server
// confirms it (patched back via the sync engine).
export type ListMember = { entityKey: string; itemId: string | null; meta: NoveltyMeta | null }

export type UserListState = {
  id: string
  name: string
  targetType: UserCustomListTargetTypeType
  isCustomTag: boolean
  customTagName: string
  removeWhenCollected: boolean
  removeWhenRead: boolean
  members: ListMember[]
}

export type UserListsMap = { [listId: string]: UserListState }

export type MembershipAction = SyncableAction & {
  op: 'add' | 'remove'
  targetType: UserCustomListTargetTypeType
  listId: string
  entityKey: string
  itemId: string | null
  meta: NoveltyMeta | null
}

export type ListAddTarget = {
  targetType: UserCustomListTargetTypeType
  releaseEntryId?: string
  relationId?: string
  meta?: NoveltyMeta
}

// Lays still-pending edits onto a clone of the server-confirmed state so the
// whole app reads one consistent membership map regardless of in-flight syncs.
export function applyActionStack(map: UserListsMap, actions: MembershipAction[]): void {
  for (const action of actions) {
    if (action.synced) continue
    const list = map[action.listId]
    if (!list) continue
    if (action.op === 'add') {
      if (!list.members.some((member) => member.entityKey === action.entityKey)) {
        list.members = [
          ...list.members,
          { entityKey: action.entityKey, itemId: action.itemId, meta: action.meta },
        ]
      }
    } else {
      list.members = list.members.filter((member) => member.entityKey !== action.entityKey)
    }
  }
}

export function cloneListsMap(lists: UserListState[]): UserListsMap {
  const map: UserListsMap = {}
  for (const list of lists) {
    map[list.id] = {
      ...list,
      members: list.members.map((member) => ({
        ...member,
        meta: member.meta ? { ...member.meta, volumes: [...member.meta.volumes] } : null,
      })),
    }
  }
  return map
}

// Via catalogQuery, not apolloClient: this is a registered catalog op, so it
// resolves from the replica offline. Going straight to the server meant adding a
// volume/box to a list offline resolved null and was silently dropped.
export async function resolveNoveltyId(relationId: string): Promise<string | null> {
  const { data } = await catalogQuery({
    query: GET_NOVELTY_ID_BY_RELATION,
    variables: { relationId },
  })
  return data?.novelties?.[0]?.id ?? null
}

export async function performMembershipSync(
  action: MembershipAction,
): Promise<Partial<MembershipAction> | void> {
  if (action.op === 'remove') {
    if (!action.itemId) return
    await apolloClient.mutate({
      mutation:
        action.targetType === UserCustomListTargetTypeType.Novelty
          ? REMOVE_NOVELTY_FROM_CUSTOM_LIST
          : REMOVE_RELEASE_ENTRY_FROM_CUSTOM_LIST,
      variables: { id: action.itemId },
    })
    return
  }
  if (action.targetType === UserCustomListTargetTypeType.Novelty) {
    const { data } = await apolloClient.mutate({
      mutation: ADD_NOVELTY_TO_CUSTOM_LIST,
      variables: { listId: action.listId, noveltyId: action.entityKey },
    })
    return { itemId: data?.createUserCustomListNoveltyItem?.id ?? null }
  }
  const { data } = await apolloClient.mutate({
    mutation: ADD_RELEASE_ENTRY_TO_CUSTOM_LIST,
    variables: { listId: action.listId, releaseEntryId: action.entityKey },
  })
  return { itemId: data?.createUserCustomListReleaseEntryItem?.id ?? null }
}

// A flattened view of one entry's collection state, derived by the caller from
// collectionMap so this module needs no user-collection import (no import cycle).
export type EntryCollectionState = {
  collectionStatus: string
  currentReadStatus: string
  ownedByVersion: { [volumeVersion: string]: number }
  readByVolume: { [volumeNumber: string]: boolean }
  miscOwnedMap: { [itemId: string]: { quantity: number } }
}

function decideByFlags(
  list: { removeWhenCollected: boolean; removeWhenRead: boolean },
  collected: boolean,
  read: boolean,
): boolean {
  if (list.removeWhenCollected && list.removeWhenRead) return collected && read
  if (list.removeWhenCollected) return collected
  return read
}

function allVolumesOwned(meta: NoveltyMeta, state: EntryCollectionState): boolean {
  if (meta.volumes.length === 0) return false
  return meta.volumes.every((volume) => (state.ownedByVersion[volume] ?? 0) > 0)
}

function allVolumesRead(meta: NoveltyMeta, state: EntryCollectionState): boolean {
  if (meta.volumes.length === 0) return false
  return meta.volumes.every((volume) => state.readByVolume[getVolumeNumberFromVersion(volume)] === true)
}

export function shouldRemoveMember(
  list: Pick<UserListState, 'targetType' | 'removeWhenCollected' | 'removeWhenRead'>,
  member: ListMember,
  state: EntryCollectionState,
): boolean {
  if (!list.removeWhenCollected && !list.removeWhenRead) return false
  if (list.targetType === UserCustomListTargetTypeType.ReleaseEntry) {
    return decideByFlags(
      list,
      state.collectionStatus === UserReleaseEntryStatusCollectionStatusType.Done,
      state.currentReadStatus === UserReleaseEntryStatusCurrentReadStatusType.Done,
    )
  }
  const meta = member.meta
  if (!meta) return false
  // A box is "collected" only when the box itself is owned (miscOwnedMap), never
  // by owning its volumes, and reading is meaningless for a box.
  if (meta.relationType === BOX_RELATION_TYPE) {
    return list.removeWhenCollected && (state.miscOwnedMap[meta.relationId]?.quantity ?? 0) > 0
  }
  return decideByFlags(list, allVolumesOwned(meta, state), allVolumesRead(meta, state))
}
